import _ from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { WaterStation } from '~/features/stations/schemas'
import { DateRange } from '~/types'
import { useTRPC } from '~/utils/trpc'
import { Resolution } from '@willie/schemas'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { formatDateLabel } from '../utils'

export const NULL_CATEGORY = 'null'
// TODO account for resolution, currently always daily

type LabelMap = Record<string, string>

// should this be suspense or not, do we want to expose it as a choice ?
// should we expose react-query options like staleTime, onSuccess, onError, etc ?

export type StationChartDataProps = {
  title: string
  stations: WaterStation[]
  /** the property on which we group the chart data, if null everything will be combined in a single category */
  partitionKey: keyof WaterStation | null
  /** use this to customize the data query cache key */
  chartKey?: string
  partitionsOptions?: { key: string; label: string; color?: string }[]
  resolution?: Resolution
} & DateRange

const makeEmptyDataForDateRange = (
  { startDate, endDate }: DateRange,
  labelMap: LabelMap,
  resolution: Resolution
): Record<string, {}> => {
  const diff = Math.ceil(endDate.diff(startDate, resolution).as(resolution))
  const dates = Array.from({ length: diff }, (_, i) => startDate.startOf(resolution).plus({ [resolution]: i }))

  return dates.reduce<Record<string, {}>>(
    (acc, dateTime) => ({
      ...acc,
      [dateTime.toISODate()!]: Object.values(labelMap).reduce((acc, label) => ({ ...acc, [label]: 0 }), {})
    }),
    {}
  )
}

const getPartitionLabel = ({
  station,
  partitionKey,
  partitionLabelMap: labelMap = {}
}: Pick<StationChartDataProps, 'partitionKey'> & { station: WaterStation; partitionLabelMap: LabelMap }) => {
  if (!partitionKey) {
    return labelMap[NULL_CATEGORY] ?? NULL_CATEGORY
  }
  return `${_.get(labelMap, `${station[partitionKey]}`, station[partitionKey])}`
}

type ConsolidatedPartitionData = {
  /** The original partition key (e.g., station property value or NULL_CATEGORY) */
  key: string
  /** The display label for the partition */
  label: string
  /** The consolidated sum value for the partition over the priod */
  value: number
}

const useStationsChartData = ({
  title,
  stations,
  partitionKey: partitionKey,
  startDate,
  endDate,
  partitionsOptions = [],
  resolution = 'day'
}: StationChartDataProps) => {
  const trpc = useTRPC()
  const locale = useCurrentLanguage()

  const { data: metersData = [], isLoading } = useQuery(
    trpc.stations.getStationsConsumptionData.queryOptions({
      stationIDs: stations.map((s) => s.id),
      startDate: startDate.toISO()!,
      endDate: endDate.toISO()!,
      resolution
    })
  )

  const labelMap = _(partitionsOptions)
    .concat([{ key: NULL_CATEGORY, label: title }])
    .groupBy('key')
    .mapValues((d) => d[0].label)
    .value()

  const keyMap = _(partitionsOptions)
    .concat([{ key: NULL_CATEGORY, label: title }])
    .keyBy('label')
    .mapValues('key')
    .value()

  const categories = useMemo(() => {
    if (partitionsOptions.length > 0) {
      return partitionsOptions.map((option) => option.label)
    } else {
      return _(stations)
        .map((station) => getPartitionLabel({ station, partitionKey: partitionKey, partitionLabelMap: labelMap }))
        .uniq()
        .value()
    }
  }, [stations, partitionKey])

  const { chartData, consolidatedOverPeriod } = useMemo(() => {
    if (!metersData) {
      return { chartData: null, consolidatedOverPeriod: [] as ConsolidatedPartitionData[] }
    }

    const emptyDataSetForRange = makeEmptyDataForDateRange({ startDate, endDate }, labelMap, resolution)

    const stationData = metersData
      .map((stationDataSet) => {
        const station = stations.find((s) => s.id === stationDataSet.stationID)!
        return _(stationDataSet.datapoints)
          .map((v) => ({
            date: v.dateTime,
            value: v.consumption,
            label: getPartitionLabel({ station, partitionKey, partitionLabelMap: labelMap })
          }))
          .value()
      })
      .flat()

    if (!stationData.length) {
      return { chartData: [], consolidatedOverPeriod: [] }
    }

    // Daily chart data
    const dailyChartData = _(stationData)
      .groupBy('date')
      .mapValues(
        (d) =>
          _(d)
            .groupBy('label')
            .mapValues((d) => _.round(_.sumBy(d, 'value'), 3))
            .value() satisfies Record<string, number>
      )
      .mergeWith(emptyDataSetForRange, (dest, empty) => ({ ...empty, ...(dest || {}) }))
      .toPairs()
      .orderBy(0)
      .map(([date, value], _, all) => ({
        isoDate: date,
        date: formatDateLabel(date, resolution, locale, all.length),
        ...value
      }))
      .value()

    // Consolidated sum per partition over the period
    const consolidatedData: ConsolidatedPartitionData[] = _(categories)
      .map((category) => {
        const categoryData = _.filter(stationData, { label: category })
        return {
          key: keyMap[category] ?? category,
          label: category,
          value: _.round(_.sumBy(categoryData, 'value'), 3)
        }
      })
      .value()

    return { chartData: dailyChartData, consolidatedOverPeriod: consolidatedData }
  }, [startDate, endDate, partitionKey, stations, labelMap, keyMap])

  return { chartData, isLoading, categories, consolidatedOverPeriod }
}

export { useStationsChartData }
